import { useState } from "react";

import {
  SwotExportButton,
} from "@swot-shared/reports/index.js";

import { ApiErrorPanel } from "@components/errors/index.js";
import { useApiError } from "@hooks/useApiError.js";
import { reportApi } from "@services/reportApi.js";

export type CmtsReportDownloadButtonProps = {
  readonly exportId: string;
  readonly filename: string;
};

const completedStatuses = new Set([
  "COMPLETED",
  "COMPLETE",
  "SUCCEEDED",
  "SUCCESS",
  "READY",
  "GENERATED",
]);

const failedStatuses = new Set([
  "FAILED",
  "ERROR",
  "CANCELLED",
  "CANCELED",
  "REJECTED",
]);

export function CmtsReportDownloadButton(
  props: CmtsReportDownloadButtonProps,
) {
  const [pending, setPending] = useState(false);
  const [statusMessage, setStatusMessage] =
    useState<string | null>(null);

  const apiError = useApiError();

  async function download(): Promise<void> {
    if (!props.exportId || pending) {
      return;
    }

    setPending(true);
    setStatusMessage("Checking export status...");
    apiError.clearError();

    try {
      await waitUntilExportReady(props.exportId, (status) => {
        setStatusMessage(
          `Export status: ${formatStatus(status)}`,
        );
      });

      setStatusMessage("Downloading file...");

      const result =
        await reportApi.downloadReportExportBlob(
          props.exportId,
        );

      downloadBlob(
        result.blob,
        props.filename || result.filename,
      );

      setStatusMessage("Download completed.");
    } catch (error: unknown) {
      setStatusMessage(null);

      apiError.setError(error, {
        operation: "report.export.download",
        entityType: "ReportExport",
        entityId: props.exportId,
      });
    } finally {
      setPending(false);
    }
  }

  return (
    <span className="cmts-report-download-button">
      <SwotExportButton
        pending={pending}
        disabled={!props.exportId || pending}
        onClick={() => void download()}
      >
        {pending
          ? "Preparing download"
          : `Download ${extensionLabel(props.filename)}`}
      </SwotExportButton>

      {statusMessage ? (
        <span className="cmts-report-download-status">
          {statusMessage}
        </span>
      ) : null}

      <ApiErrorPanel
        error={apiError.error}
        compact
        onRetry={() => void download()}
      />
    </span>
  );
}

async function waitUntilExportReady(
  exportId: string,
  onStatus: (status: string) => void,
): Promise<void> {
  const maximumAttempts = 15;
  const pollingIntervalMs = 1500;

  for (
    let attempt = 1;
    attempt <= maximumAttempts;
    attempt += 1
  ) {
    const job =
      await reportApi.getReportExportStatus(exportId);

    const status = String(
      job.status ??
      job.exportStatus ??
      job.state ??
      "",
    ).toUpperCase();

    onStatus(status || "PROCESSING");

    if (completedStatuses.has(status)) {
      return;
    }

    if (failedStatuses.has(status)) {
      throw new Error(
        `Report export ended with status '${status}'.`,
      );
    }

    /*
     * Some synchronous export endpoints return an ID without
     * exposing a status value. Allow immediate download.
     */
    if (!status && attempt === 1) {
      return;
    }

    await delay(pollingIntervalMs);
  }

  throw new Error(
    "The report is still being generated. Please retry the download shortly.",
  );
}

function downloadBlob(
  blob: Blob,
  filename: string,
): void {
  if (blob.size === 0) {
    throw new Error(
      "The backend returned an empty export file.",
    );
  }

  const objectUrl =
    URL.createObjectURL(blob);

  const anchor =
    document.createElement("a");

  anchor.href = objectUrl;
  anchor.download =
    sanitizeFilename(filename);
  anchor.rel = "noopener";
  anchor.style.display = "none";

  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  window.setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
  }, 1000);
}

function sanitizeFilename(
  filename: string,
): string {
  const cleaned = filename
    .replace(/[\\/:*?"<>|\u0000-\u001F]/g, "-")
    .replace(/\s+/g, " ")
    .trim();

  return cleaned || "cmts-report-export.bin";
}

function extensionLabel(
  filename: string,
): string {
  const extension =
    filename.split(".").pop()?.toUpperCase();

  return extension &&
    ["CSV", "XLSX", "JSON"].includes(extension)
    ? extension
    : "export";
}

function formatStatus(
  status: string,
): string {
  return status
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(
      /\b\w/g,
      (character) => character.toUpperCase(),
    );
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });
}